<template>
    <div class="card" v-show="loaded">

        <div class="card-header border-0">
            <div class="row">
                <div class="col-6">
                    <h3 class="text-uppercase">{{ $t('grids.outdated') }}</h3>
                    <small class="text-muted">{{ $t('grids.outdated-meaning', { age: dataConservation }) }}</small>
                </div>
                <div class="col-6 text-right">
                    <base-button type="danger" size="sm" @click="deleteAll()">
                        <span class="btn-inner--icon"><i class="tim-icons icon-simple-remove"></i></span>
                        <span class="btn-inner--text">{{ $t('common.delete-all') }}</span>
                    </base-button>
                </div>
            </div>
        </div>

        <p class="card-body pt-0 text-sm" v-if="empty">{{ $t('grids.no-grid') }}</p>

        <div class="pt-2" v-if="!empty">
            
            <table-control :paging="ctrl.paging" :search="true" v-model="search"></table-control>

            <el-table class="table-responsive table-flush"
                    header-row-class-name="thead-light"
                    :data="ctrl.rows">

                <el-table-column :label="$t('common.name')">
                    <template v-slot="{row}">
                        <h4 class="mb-0">
                            <a href="#" @click.prevent="open(row)">
                                {{ row.name }}
                            </a>
                        </h4>
                    </template>
                </el-table-column>

                <el-table-column :label="$t('common.edition')">
                    <template v-slot="{row}">
                        <small v-if="row.edition">
                            {{ row.edition }}
                        </small>
                    </template>
                </el-table-column>

                <el-table-column :label="$t('common.code')">
                    <template v-slot="{row}">
                        {{ row.code }}
                    </template>
                </el-table-column>

                <el-table-column :label="$t('common.lang')">
                    <template v-slot="{row}">
                        {{ row.lang }}
                    </template>
                </el-table-column>

                <el-table-column :label="$t('common.position') + ' (EN/FR)'">
                    <template v-slot="{row}">
                        {{ gridSimulationRole(row) }}
                    </template>
                </el-table-column>

                <el-table-column :label="$t('common.owner')">
                    <template v-slot="{row}">
                        {{ owners[row.owner_id] }}
                        <span v-if="row.shared">
                            <i class="fas fa-share ml-1"></i>
                        </span>
                    </template>
                </el-table-column>

                <el-table-column align="right" width="60" class-name="actions">
                    <template v-slot="{row}">
                        <a href="#" class="table-action text-danger" @click.prevent="ctrl.delete(row.id)">
                            <i class="tim-icons icon-simple-remove"></i>
                        </a>
                    </template>
                </el-table-column>
                
            </el-table>

            <table-pagination :paging="ctrl.paging"></table-pagination>
        </div>

        <portal to="modals">

            <confirm-modal v-model="ctrl.modals.confirmDelete">
                <p>{{ $t('grids.delete-confirm') }}</p>
            </confirm-modal>

            <confirm-modal v-model="ctrl.modals.confirmDeleteAll">
                <p>{{ $t('grids.delete-all-confirm') }}</p>
            </confirm-modal>

        </portal>

    </div>
</template>

<script>
    import CrudController from '@trax/classes/CrudController'

    export default {

        data() {
            return {
                loaded: false,
                ctrl: new CrudController({
                    endpoint: '/socrates/api/front/grids',
                    mapData: this.mapData,
                    unmapData: this.unmapData,
                    modals: ['confirmDeleteAll'],
                    paging: {
                        perPage: 10,
                        perPageOptions: [5, 10, 25, 50],
                        total: 0
                    },
                    query: {
                        sort: ['-created_at', 'name'],
                        include: ['simulation_roles', 'owners'],
                        filters: { outdated: true },
                    },
                    errors: {
                        '423-delete': 'grids.423-delete',
                    }
                }),
                search: '',
                owners: {},
                simulationRoles: {},
                next: false
            }
        },

        computed: {

            dataConservation() {
                return this.$auth['config'].data_conservation
            },

            empty() {
                return this.ctrl.rows.length ==0 && !this.search
            },
        },

        created() {
            this.ctrl.init(this, 'ctrl').then(resp => {
                this.loaded = true
                this.owners = Array.isArray(resp.data.included.owners) ? {} : resp.data.included.owners
                this.simulationRoles = resp.data.included.simulation_roles
            })
        },

        watch: {
            search(val) {
                this.ctrl.filterMore({ name: {$text: val} })
            }
        },

        methods: {
            
            deleteAll() {
                this.ctrl.modals.open('confirmDeleteAll').then(confirmResponse => {
                    if (confirmResponse.confirmed) {
                        this.$loader.show()
                        axios.delete(
                            '/socrates/api/front/grids',
                            { params: { filters: { outdated: true } }}
                        )
                        .then(response => {
                            this.ctrl.list()
                            this.$loader.hide()
                        }).catch(error => {
                            this.ctrl.list()
                            this.$loader.hide()
                            //this.$auth.handleErrorVm(error, this)
                        })
                    }
                })
            },

            gridSimulationRole(grid) {
                if (!this.simulationRoles[grid.simulation_role]) {
                    return ''
                }
                return this.simulationRoles[grid.simulation_role].en + '/' + this.simulationRoles[grid.simulation_role].fr
            },

            open(row) {
                this.$router.push({ name: 'admin-competency-grid', params: { id: row.id } })
            },

            mapData(cohort) {
                return {
                    id: cohort ? cohort.id : null,
                    name: cohort ? cohort.name : null,
                    description: cohort ? cohort.description : null,
                    code: cohort ? cohort.code : null,
                    status: cohort ? cohort.status : null,
                    edition: cohort ? cohort.edition : null,
                    shared: cohort ? cohort.shared : false,
                    owner_id: cohort ? cohort.owner_id : null,
                    lang: cohort ? cohort.lang : 'en',
                    simulation_role: cohort ? cohort.simulation_role : null,
                }
            },

            unmapData(data) {
                return data
            }
        }
    }
</script>

<style scoped>

    .card >>> th,
    .card >>> td {
        padding-right: 0;
    }
    .card >>> th.actions,
    .card >>> td.actions {
        padding-left: 0;
        padding-right: 1.5rem;
    }

</style>
